<template>
	<Card class="h-full flex flex-col">
	<CardHeader class="flex flex-row items-center justify-between p-4 border-b">
	<CardTitle class="text-lg font-medium">
	Script Editor
	</CardTitle>
	<div class="flex items-center gap-2">
		<Select v-model="currentLanguage">
			<SelectTrigger class="w-[120px]">
			<SelectValue placeholder="Language" />
			</SelectTrigger>
			<SelectContent>
			<SelectGroup>
			<SelectItem 
				     v-for="lang in supportedLanguages"
				     :key="lang"
				     :value="lang"
				     >
				     {{ lang }}
			</SelectItem>
			</SelectGroup>
			</SelectContent>
		</Select>

		<Button 
				     v-if="showFormatButton"
				     variant="outline"
				     size="sm"
				     @click="formatCode"
				     >
				     <Wand2 class="w-4 h-4 mr-2" />
				     Format
		</Button>
	</div>
	</CardHeader>
	<CardContent class="flex-1 p-0 overflow-hidden">
	<MonacoEditor
			v-model="templateCode"
			language="html"
			:options="{
				  minimap: { enabled: false },
				  fontSize: 14,
				  tabSize: 2
				  }"
			class="h-full"
			/>
	</CardContent>
	</Card>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { Wand2 } from 'lucide-vue-next'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle, } from '@/components/ui/card';
import { Button } from '@/components/ui/button'
import MonacoEditor from '@/components/ui/MonacoEditor.vue'
import { useDebounceFn } from '@vueuse/core'

// Types
type EditorLanguage = 'javascript' | 'typescript' | 'json'

interface Props {
	modelValue: string
	showFormatButton?: boolean
	languages?: EditorLanguage[]
}
const templateCode = ref('');
const props = withDefaults(defineProps<Props>(), {
	showFormatButton: true,
	languages: () => ['javascript', 'typescript', 'json']
})

const emit = defineEmits<{
	(e: 'update:modelValue', value: string): void
	(e: 'change', value: string): void
}>()

// Refs
const editorRef = ref<InstanceType<typeof MonacoEditor> | null>(null)
const scriptCode = ref(props.modelValue)
const currentLanguage = ref<EditorLanguage>('javascript')

// Computed
const supportedLanguages = computed(() => props.languages)
const editorOptions = computed(() => ({
	minimap: { enabled: false },
	fontSize: 14,
	lineNumbers: 'on',
	wordWrap: 'on',
	automaticLayout: true,
	tabSize: 2,
	theme: 'vs-dark',
	scrollBeyondLastLine: false,
	fontFamily: 'var(--font-mono)',
	bracketPairColorization: {
		enabled: true
	}
}))

// Handlers
const debouncedEmit = useDebounceFn((value: string) => {
	emit('update:modelValue', value)
	emit('change', value)
}, 300)

const handleCodeChange = (value: string) => {
	scriptCode.value = value
	debouncedEmit(value)
}

const formatCode = async () => {
	const editor = editorRef.value?.getEditorInstance()
	if (!editor) return

	try {
		await editor.getAction('editor.action.formatDocument')?.run()
		const formattedValue = editor.getValue()
		scriptCode.value = formattedValue
		emit('update:modelValue', formattedValue)
	} catch (error) {
		console.error('Formatting error:', error)
	}
}

// Watchers
watch(() => props.modelValue, (newValue) => {
	if (newValue !== scriptCode.value) {
		scriptCode.value = newValue
	}
})
</script>

<style>
/* Monaco Editor customizations to match shadcn */
.monaco-editor {
	--monaco-font-family: var(--font-mono);
	--monaco-background: hsl(var(--background));
}

.monaco-editor .margin {
	background-color: hsl(var(--background)) !important;
}
</style>
